<template>
  <div class="theme-stage ws-theme-stage" aria-hidden="true">
    <template v-for="layer in sceneLayers" :key="layer.id">
      <video
        v-if="layer.type === 'video' && assetUrl(layer.assetId)"
        class="theme-stage__layer theme-stage__video"
        :class="motionClass(layer.motion)"
        :style="layerStyle(layer)"
        :src="assetUrl(layer.assetId)"
        autoplay muted loop playsinline
      />
      <div
        v-else
        class="theme-stage__layer"
        :class="[layer.type, motionClass(layer.motion)]"
        :style="layerStyle(layer)"
      />
    </template>
    <img
      v-for="decoration in variant.decorations"
      :key="decoration.id"
      v-show="assetUrl(decoration.assetId)"
      class="theme-stage__decoration"
      :class="[`theme-stage__decoration--${decoration.anchor}`, `theme-stage__decoration--${decoration.width}`, motionClass(decoration.motion), { 'theme-stage__decoration--mobile-hidden': decoration.hideOnMobile }]"
      :src="assetUrl(decoration.assetId)"
      :style="{ opacity: decoration.opacity }"
      alt=""
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { CSSProperties } from 'vue';
import type { ThemeSceneLayer } from 'shared';
import { createDefaultThemeManifest } from 'shared';
import { themeAssetUrl } from '@/api/theme.api';
import { useWorkspaceThemeStore } from '@/stores/workspace-theme.store';

const theme = useWorkspaceThemeStore();
const manifest = computed(() => theme.runtime?.manifest ?? createDefaultThemeManifest());
const variant = computed(() => theme.appearance.mode === 'dark' ? manifest.value.dark ?? manifest.value.light : manifest.value.light);
const resolvedAssetUrls = computed(() => theme.runtime?.assetUrls ?? {});
const sceneLayers = computed(() => theme.runtime?.backgroundUrl
  ? [...variant.value.scene.layers.filter((layer) => !['image', 'texture', 'video'].includes(layer.type)), { id: 'personal-background', type: 'image' as const, assetId: '__personal-background__', opacity: 1, position: 'cover' as const }]
  : variant.value.scene.layers);

function assetUrl(id?: string): string {
  const url = id === '__personal-background__' ? theme.runtime?.backgroundUrl : id ? resolvedAssetUrls.value[id] : '';
  return url ? themeAssetUrl(url) : '';
}
function motionClass(motion: ThemeSceneLayer['motion'] | 'none' | 'float' | 'drift' | 'pulse' | undefined): string { return motion && motion !== 'none' ? `theme-stage--${motion}` : ''; }
function layerStyle(layer: ThemeSceneLayer): CSSProperties {
  const background = layer.type === 'gradient'
    ? `radial-gradient(circle at 12% 12%, ${layer.colors?.[0]}, transparent 58%), radial-gradient(circle at 88% 22%, ${layer.colors?.[1]}, transparent 62%), linear-gradient(135deg, ${layer.colors?.[0]}, ${layer.colors?.[2] ?? layer.colors?.[1]})`
    : layer.type === 'color' ? layer.color
      : assetUrl(layer.assetId) ? `url("${assetUrl(layer.assetId)}")` : 'none';
  return { opacity: layer.opacity, background, backgroundPosition: 'center', backgroundSize: layer.position === 'contain' ? 'contain' : 'cover', backgroundRepeat: 'no-repeat' };
}

</script>

<style scoped>
.theme-stage { position: fixed; z-index: 0; inset: 0; overflow: hidden; pointer-events: none; background: var(--ws-color-canvas); }
.theme-stage__layer { position: absolute; inset: -5%; will-change: transform; }
.theme-stage__video { width: 110%; height: 110%; object-fit: cover; }
.theme-stage__layer.texture { mix-blend-mode: soft-light; }
.theme-stage__decoration { position: absolute; z-index: 2; max-width: min(42vw, 560px); max-height: min(62vh, 680px); object-fit: contain; filter: drop-shadow(0 18px 34px rgb(10 30 70 / 16%)); }
.theme-stage__decoration--top-left { top: max(18px, env(safe-area-inset-top)); left: max(18px, env(safe-area-inset-left)); }
.theme-stage__decoration--top-right { top: max(18px, env(safe-area-inset-top)); right: max(18px, env(safe-area-inset-right)); }
.theme-stage__decoration--bottom-left { bottom: max(18px, env(safe-area-inset-bottom)); left: max(18px, env(safe-area-inset-left)); }
.theme-stage__decoration--bottom-right { right: max(18px, env(safe-area-inset-right)); bottom: max(18px, env(safe-area-inset-bottom)); }
.theme-stage__decoration--center { top: 50%; left: 50%; --theme-stage-rest-transform: translate(-50%, -50%); transform: var(--theme-stage-rest-transform); }
.theme-stage__decoration--small { width: min(20vw, 240px); }.theme-stage__decoration--medium { width: min(30vw, 380px); }.theme-stage__decoration--large { width: min(42vw, 560px); }
.theme-stage--float { animation: theme-float 7s ease-in-out infinite; }.theme-stage--drift { animation: theme-drift 18s ease-in-out infinite alternate; }.theme-stage--pulse { animation: theme-pulse 3.6s ease-in-out infinite; }
@keyframes theme-float { 50% { transform: var(--theme-stage-rest-transform, translate3d(0, 0, 0)) translateY(-16px); } } @keyframes theme-drift { to { transform: var(--theme-stage-rest-transform, translate3d(0, 0, 0)) translate3d(3%, -2%, 0) scale(1.04); } } @keyframes theme-pulse { 50% { opacity: .76; } }
@media (max-width: 720px) { .theme-stage__decoration--mobile-hidden { display: none; }.theme-stage__decoration { max-width: 56vw; } }
</style>
